import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getDashboardTitle } from '../config/navigation';
import DesktopNavigation from '../components/DesktopNavigation';
import MobileNavigation from '../components/MobileNavigation';
import '../styles/AdminDashboard.css';

/**
 * Centralized Desktop Layout Component
 * 
 * Provides consistent layout structure with:
 * - Role-based sidebar navigation for desktop
 * - Mobile navigation for mobile devices  
 * - Responsive design that adapts to screen size
 * - Consistent header and main content area
 * 
 * Usage:
 * <DesktopLayout activeNavItem="properties">
 *   <YourPageContent />
 * </DesktopLayout>
 */
const DesktopLayout = ({ 
  children, 
  activeNavItem = null,
  setActiveNavItem = null,
  showHeader = true,
  headerTitle = null
}) => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [localActiveNavItem, setLocalActiveNavItem] = useState(activeNavItem);
  
  // Use provided setActiveNavItem or local state
  const handleSetActiveNavItem = setActiveNavItem || setLocalActiveNavItem;
  const currentActiveNavItem = activeNavItem || localActiveNavItem;
  
  // Get appropriate header title
  const getHeaderTitle = () => {
    if (headerTitle) return headerTitle;
    return getDashboardTitle(user?.role);
  };

  // Logout handler — AuthContext owns the session lifecycle; this only navigates
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile Navigation - Always rendered at top level for mobile */}
      <MobileNavigation
        user={user}
        activeNavItem={currentActiveNavItem}
        setActiveNavItem={handleSetActiveNavItem}
        onLogout={handleLogout}
      />

      {/* Desktop Layout */}
      <div className="admin-dashboard">
        {showHeader && (
          <div className="admin-header">
            <div className="admin-header-content">
              <h2>{getHeaderTitle()}</h2>
              <div className="admin-header-actions">
                {['admin', 'ops', 'ops-manager'].includes(user?.role) && (
                  <button
                    className="btn"
                    onClick={() => navigate('/alerts')}
                  >
                    <i className="bi bi-bell"></i> جميع التنبيهات
                  </button>
                )}
                <button className="btn" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-left"></i> تسجيل الخروج
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Layout Container */}
        <div className="admin-layout">
          <DesktopNavigation
            user={user}
            activeNavItem={currentActiveNavItem}
            setActiveNavItem={handleSetActiveNavItem}
          />

          {/* Main Content Area */}
          <main className="admin-content">
            {children}
          </main>
        </div>
      </div>
    </>
  );
};

export default DesktopLayout;